import React, { createContext, useContext, useMemo } from 'react';
import type { AlertViewer } from '../constants/alertTheme';

interface SessionViewerContextValue {
  /**
   * The user does not run this call: a watcher (a bot they did not add, or a
   * desktop call) or a co-seller on someone's desktop call with their own mic.
   * Every control that changes the owner's call is hidden: card feedback and
   * question edits write to the owner's saved session, which the backend
   * scopes to the owner (co-sellers can only leave, D3 2026-10-05).
   */
  isWatcher: boolean;
  /**
   * The viewer's own participant on this call: a web co-seller's
   * `salesperson_<n>`, the bot owner's "Me" participant. Null for a watcher
   * (a watcher is never "You") and while the owner's is not known.
   */
  ownParticipantId: string | null;
}

const SessionViewerContext = createContext<SessionViewerContextValue>({ isWatcher: false, ownParticipantId: null });

export const SessionViewerProvider: React.FC<{
  isWatcher: boolean;
  ownParticipantId?: string | null;
  children: React.ReactNode;
}> = ({ isWatcher, ownParticipantId = null, children }) => {
  const value = useMemo(() => ({ isWatcher, ownParticipantId }), [isWatcher, ownParticipantId]);
  return <SessionViewerContext.Provider value={value}>{children}</SessionViewerContext.Provider>;
};

/** True while the page shows someone else's call (watched or co-sold). Outside a provider: false. */
export const useIsWatcher = (): boolean => useContext(SessionViewerContext).isWatcher;

/**
 * Who is looking at the live alerts: their own participant, and whether they
 * run the call. Labels ("You said") and the speech-rate banner read it.
 */
export const useAlertViewer = (): AlertViewer => {
  const { isWatcher, ownParticipantId } = useContext(SessionViewerContext);
  return useMemo(() => ({ ownParticipantId, isCallOwner: !isWatcher }), [isWatcher, ownParticipantId]);
};
